import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import _get from 'lodash/get';
import { RootState } from '../../configuration';
import Avatar from '../../components/avatar/avatar.component';
import {
  getClanDetail,
  joinClan,
  leaveClan,
  kickMember,
  setRole,
  expandClan,
  disbandClan,
  Clan,
  ClanMember,
  ClanRole,
  CLAN_ROLE_LABEL,
} from '../../api/group/clan.api';
import { ArenaClanBadge, getClanBadges } from '../../api/group/arena.api';
import ClanChatPanel from './components/clan-chat-panel';
import ClanTreasurySection from './components/clan-treasury-section';
import ClanCategoryHolds from './components/clan-category-holds';
import ClanLevelCard from './components/clan-level-card';
import ReportClanDialog from '../../components/report-clan-dialog/report-clan-dialog.component';
import {
  getClanPosts,
  ClanPostItem,
  ClanPostStats,
} from '../../api/posts/clan-posts.api';
import { FileText, ThumbsUp, MessageCircle, Eye } from 'lucide-react';

const BTN = 'rounded-full px-4 py-2 text-[14px] font-medium transition disabled:opacity-60';
const CARD_GRAD = [
  'from-[rgba(162,231,79,0.7)] to-[rgba(236,245,171,0.7)]',
  'from-[rgba(139,193,255,0.7)] to-[rgba(206,255,254,0.7)]',
  'from-[rgba(244,197,175,0.7)] to-[rgba(255,235,206,0.7)]',
];

const roleBadge = (role: ClanRole) => {
  const map: Record<number, string> = {
    0: 'bg-[#FEF3C7] text-[#B45309]',
    1: 'bg-[#DBEAFE] text-[#1D4ED8]',
    2: 'bg-[#F1F5F9] text-[#475569]',
  };
  return map[role];
};

const ClanDetailPage: React.FC = () => {
  const { clanId } = useParams<{ clanId: string }>();
  const navigate = useNavigate();
  const profile = useSelector((s: RootState) => s.features.auth.profile);
  const myUserId = _get(profile, 'userId', '') as string;

  const [clan, setClan] = useState<Clan | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [clanPosts, setClanPosts] = useState<ClanPostItem[]>([]);
  const [clanStats, setClanStats] = useState<ClanPostStats | null>(null);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const load = async () => {
    if (!clanId) return;
    setLoading(true);
    try {
      setClan(await getClanDetail(clanId));
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được bang hội');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clanId]);

  const [badges, setBadges] = useState<ArenaClanBadge[]>([]);
  useEffect(() => {
    if (!clanId) return;
    getClanBadges(clanId)
      .then((b) => setBadges(b || []))
      .catch(() => setBadges([]));
  }, [clanId]);

  // Bài viết của các thành viên trong bang + thống kê tương tác.
  useEffect(() => {
    if (!clan) return;
    const ids = clan.members.map((m) => m.userId).filter(Boolean);
    if (ids.length === 0) return;
    setLoadingPosts(true);
    getClanPosts(ids, 1, 10)
      .then((res) => {
        setClanPosts(res.posts || []);
        setClanStats(res.stats || null);
      })
      .catch(() => {
        setClanPosts([]);
        setClanStats(null);
      })
      .finally(() => setLoadingPosts(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clan]);

  const run = async (fn: () => Promise<any>, okMsg: string, after?: 'reload' | 'leave') => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      if (after === 'leave') navigate('/clan');
      else await load();
    } catch (e: any) {
      toast.error(e?.message || 'Không thực hiện được');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spin />
      </div>
    );
  }
  if (!clan) {
    return <div className="p-10 text-center text-[#657384]">Clan không tồn tại.</div>;
  }

  const me = clan.members.find((m) => m.userId === myUserId);
  const isKing = me?.role === 0;
  const isCaptain = me?.role === 1;
  const isMember = !!me;
  const memberCap = clan.effectiveMemberLimit ?? clan.memberLimit;
  const full = clan.memberCount >= memberCap;
  const captainCount = clan.members.filter((m) => m.role === 1).length;

  const confirm = (msg: string) => window.confirm(msg);

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6 px-6 py-6 pb-16">
      {/* Banner */}
      <div
        className="relative h-[220px] w-full overflow-hidden rounded-[20px]"
        style={{ background: clan.banner ? `url(${clan.banner}) center/cover` : `linear-gradient(135deg, ${clan.crestColor || '#066bff'}, #19052f)` }}
      >
        <button
          className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 text-[13px] font-medium"
          onClick={() => navigate('/clan')}
        >
          ← Danh sách bang
        </button>
      </div>

      {/* Slogan + header */}
      {clan.slogan && (
        <p className="text-center text-[22px] font-semibold italic text-[#19052f]">"{clan.slogan}"</p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-block size-9 rounded-full border-2 border-white shadow" style={{ background: clan.crestColor || '#066bff' }} />
          <div>
            <div className="text-[22px] font-bold text-[#19052f]">{clan.name}</div>
            <div className="text-[12px] text-[#657384]">
              Màu chủ đạo:{' '}
              <span className="inline-block size-3 translate-y-0.5 rounded-full" style={{ background: clan.crestColor || '#066bff' }} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isMember && !isKing && (
            <button
              className={`${BTN} border border-[#E11D48] text-[#E11D48]`}
              onClick={() => confirm('Rời bang hội này?') && run(() => leaveClan(clan.id), 'Đã rời bang', 'leave')}
              disabled={busy}
            >
              Rời bang
            </button>
          )}
          {!isMember && (
            <button
              className={`${BTN} bg-[#066bff] text-white`}
              disabled={busy || full}
              onClick={() => run(() => joinClan(clan.id), 'Đã vào bang')}
            >
              {full ? 'Bang đã đầy' : 'Vào bang'}
            </button>
          )}
          <button
            className={`${BTN} border border-[#DEE0E6] dark:border-slate-700 text-[#19052f]`}
            onClick={() => {
              navigator.clipboard?.writeText(`${location.origin}/clan/${clan.id}`);
              toast.success('Đã sao chép link mời');
            }}
          >
            + Mời thành viên
          </button>
          {!isKing && (
            <button
              className={`${BTN} border border-[#DEE0E6] dark:border-slate-700 text-[#E11D48]`}
              onClick={() => setReportOpen(true)}
              title="Báo cáo bang hội vi phạm"
            >
              ⚑ Báo cáo
            </button>
          )}
          {isKing && (
            <button
              className={`${BTN} border border-[#E11D48] text-[#E11D48]`}
              onClick={() => confirm('GIẢI TÁN bang hội? Toàn bộ thành viên sẽ bị xoá khỏi bang.') && run(() => disbandClan(clan.id), 'Đã giải tán bang', 'leave')}
              disabled={busy}
            >
              Giải tán
            </button>
          )}
        </div>
      </div>

      {/* Thẻ chỉ số */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className={`flex flex-col justify-center gap-1 rounded-[16px] border-[1.5px] border-white/40 bg-gradient-to-b ${CARD_GRAD[0]} p-5`}>
          <div className="text-[15px] font-medium text-black">Thành viên</div>
          <div className="text-[26px] font-bold text-black">{clan.memberCount} / {memberCap}</div>
          {isKing && clan.memberLimit < 100 && (
            <button
              className="mt-1 w-fit rounded-full bg-[#066bff] px-3 py-1 text-[13px] font-medium text-white"
              onClick={() => confirm('Mở rộng bang lên 100 thành viên? Cần 1 vé "Mở rộng bang hội".') && run(() => expandClan(clan.id), 'Đã mở rộng bang')}
              disabled={busy}
            >
              Mở rộng
            </button>
          )}
        </div>
        <div className={`flex flex-col justify-center gap-1 rounded-[16px] border-[1.5px] border-white/40 bg-gradient-to-b ${CARD_GRAD[1]} p-5`}>
          <div className="text-[15px] font-medium text-black">Tổng điểm mùa</div>
          <div className="text-[26px] font-bold text-black">—</div>
          <div className="text-[12px] text-black/60">Sắp có (mùa giải)</div>
        </div>
        <div className={`flex flex-col justify-center gap-1 rounded-[16px] border-[1.5px] border-white/40 bg-gradient-to-b ${CARD_GRAD[2]} p-5`}>
          <div className="text-[15px] font-medium text-black">Thứ hạng</div>
          <div className="text-[26px] font-bold text-black">—</div>
          <div className="text-[12px] text-black/60">Sắp có (mùa giải)</div>
        </div>
      </div>

      <ClanLevelCard level={clan.level} levelPoints={clan.levelPoints} nextLevelPoints={clan.nextLevelPoints} />

      {/* Thành viên */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[20px] font-semibold text-[#19052f]">Thành viên bang hội ({clan.members.length})</h2>
        <div className="flex flex-col divide-y divide-[#F1F5F9] rounded-[16px] bg-white p-2 shadow-[4px_4px_44px_rgba(0,0,0,0.05)]">
          {clan.members.map((m) => (
            <MemberRow
              key={m.userId}
              m={m}
              self={m.userId === myUserId}
              canManage={{ isKing: !!isKing, isCaptain: !!isCaptain }}
              captainCount={captainCount}
              busy={busy}
              onKick={() => confirm('Kick thành viên này?') && run(() => kickMember(clan.id, m.userId), 'Đã kick')}
              onSetRole={(role) => run(() => setRole(clan.id, m.userId, role), 'Đã cập nhật vai trò')}
            />
          ))}
        </div>
      </section>

      {(isKing || isCaptain) && <ClanCategoryHolds clanId={clan.id} memberIds={clan.members.map((m) => m.userId)} />}

      {isMember && <ClanTreasurySection members={clan.members} myUserId={myUserId} canManage={!!(isKing || isCaptain)} />}

      {/* Bài viết của bang & thống kê tương tác */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[20px] font-semibold text-[#19052f] dark:text-white">
          Bài viết của bang & thống kê tương tác
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard icon={<FileText size={18} />} label="Tổng bài" value={clanStats?.totalPosts ?? 0} />
          <StatCard icon={<ThumbsUp size={18} />} label="Lượt thích" value={clanStats?.totalLikes ?? 0} />
          <StatCard icon={<MessageCircle size={18} />} label="Bình luận" value={clanStats?.totalComments ?? 0} />
          <StatCard icon={<Eye size={18} />} label="Lượt xem" value={clanStats?.totalViews ?? 0} />
        </div>
        <div className="flex flex-col divide-y divide-[#F1F5F9] rounded-[16px] bg-white p-2 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:divide-slate-700 dark:bg-slate-800">
          {loadingPosts ? (
            <div className="py-8 text-center text-[#657384]">Đang tải bài viết…</div>
          ) : clanPosts.length === 0 ? (
            <div className="py-8 text-center text-[#657384]">Thành viên trong bang chưa có bài viết nào.</div>
          ) : (
            clanPosts.map((p) => <ClanPostRow key={p.id} p={p} />)
          )}
        </div>
      </section>

      {badges.length > 0 && (
        <section className="flex flex-col gap-3" data-testid="clan-badges">
          <h2 className="text-[20px] font-semibold text-[#19052f] dark:text-white">Huy hiệu quý</h2>
          <div className="flex flex-wrap gap-2">
            {badges.map((b) => (
              <span
                key={`${b.quarter}-${b.rank}`}
                className="rounded-full bg-[#eaf2ff] px-4 py-1.5 text-[14px] font-medium text-[#066bff] dark:bg-slate-700 dark:text-sky-300"
              >
                {b.title}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Chat bang hội — chỉ thành viên mới thấy */}
      {isMember && clan.id && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[20px] font-semibold text-[#19052f] dark:text-white">Trò chuyện</h2>
          <ClanChatPanel clanId={clan.id} />
        </section>
      )}

      <ReportClanDialog
        visible={reportOpen}
        onClose={() => setReportOpen(false)}
        clanId={clan.id}
        clanName={clan.name || undefined}
      />
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: number }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex flex-col gap-1 rounded-[14px] border border-[#EAEFFA] bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-center gap-2 text-[13px] text-[#657384] dark:text-slate-400">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-[22px] font-bold text-[#19052f] dark:text-white">
      {value.toLocaleString('vi-VN')}
    </div>
  </div>
);

const ClanPostRow: React.FC<{ p: ClanPostItem }> = ({ p }) => {
  const preview = (p.title || p.content || '').trim() || '(Không có nội dung)';
  return (
    <div className="flex items-start gap-3 px-2 py-3">
      <Avatar avatar={p.userAvatar || ''} label={(p.userFullName || p.username || 'U').slice(0, 2)} size="xs" userId={p.userId} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {p.username ? (
            <Link to={`/user/${p.username}`} className="text-[14px] font-semibold text-[#19052f] hover:underline dark:text-white">
              {p.userFullName || p.username}
            </Link>
          ) : (
            <span className="text-[14px] font-semibold text-[#19052f] dark:text-white">{p.userFullName || 'Thành viên'}</span>
          )}
          <span className="text-[12px] text-[#657384]">
            {new Date(p.createdAt).toLocaleDateString('vi-VN')}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-[14px] text-[#3b4351] dark:text-slate-300">{preview}</p>
        <div className="mt-1.5 flex items-center gap-4 text-[12px] text-[#657384] dark:text-slate-400">
          <span className="flex items-center gap-1"><ThumbsUp size={14} /> {p.likesCount}</span>
          <span className="flex items-center gap-1"><MessageCircle size={14} /> {p.commentsCount}</span>
          <span className="flex items-center gap-1"><Eye size={14} /> {p.viewCount}</span>
        </div>
      </div>
    </div>
  );
};

const MemberRow: React.FC<{
  m: ClanMember;
  self: boolean;
  canManage: { isKing: boolean; isCaptain: boolean };
  captainCount: number;
  busy: boolean;
  onKick: () => void;
  onSetRole: (role: ClanRole) => void;
}> = ({ m, self, canManage, captainCount, busy, onKick, onSetRole }) => {
  const { isKing, isCaptain } = canManage;
  // Quyền: King thao tác mọi thành viên khác (không phải mình); Captain chỉ kick Member.
  const kingCanManage = isKing && !self && m.role !== 0;
  const captainCanKick = isCaptain && m.role === 2 && !self;

  return (
    <div className="flex items-center gap-3 px-2 py-2.5">
      <Avatar avatar="" label={(m.userId || 'TV').slice(0, 2)} size="xs" userId={m.userId} />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-medium text-[#19052f]">
            {self ? 'Bạn' : `Thành viên …${(m.userId || '').slice(-4)}`}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${roleBadge(m.role)}`}>
            {CLAN_ROLE_LABEL[m.role]}
          </span>
        </div>
      </div>
      {kingCanManage && (
        <div className="flex flex-wrap gap-1">
          {m.role === 2 && captainCount < 3 && (
            <button className="rounded-full bg-[#DBEAFE] px-2.5 py-1 text-[12px] text-[#1D4ED8]" disabled={busy} onClick={() => onSetRole(1)}>
              Phong Captain
            </button>
          )}
          {m.role === 1 && (
            <button className="rounded-full bg-[#F1F5F9] px-2.5 py-1 text-[12px] text-[#475569]" disabled={busy} onClick={() => onSetRole(2)}>
              Gỡ Captain
            </button>
          )}
          <button
            className="rounded-full bg-[#FEF3C7] px-2.5 py-1 text-[12px] text-[#B45309]"
            disabled={busy}
            onClick={() => window.confirm('Trao ngôi King cho thành viên này? Bạn sẽ thành Member.') && onSetRole(0)}
          >
            Trao ngôi King
          </button>
          <button className="rounded-full bg-[#FEE2E2] px-2.5 py-1 text-[12px] text-[#E11D48]" disabled={busy} onClick={onKick}>
            Kick
          </button>
        </div>
      )}
      {captainCanKick && (
        <button className="rounded-full bg-[#FEE2E2] px-2.5 py-1 text-[12px] text-[#E11D48]" disabled={busy} onClick={onKick}>
          Kick
        </button>
      )}
    </div>
  );
};

export default ClanDetailPage;
